import { ComparisonType, GameObject, GameType, MetricDirection, RankingByLogin } from "../game.models";
import { Component, Input, LOCALE_ID, Inject, ElementRef, ViewChild, ChangeDetectorRef } from "@angular/core";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CvNumberLocalizePipe } from "src/app/reusables/pipes/cv-number-localize.pipe";
import { formatDate } from '@angular/common';
import { GamesService } from "src/app/gamification/command-center/services/games.service";
import { TooltipDirective } from "ngx-bootstrap/tooltip";
import { NavigationService } from 'src/app/navigation/navigation.service';

@Component({
    selector: 'cxone-game-card',
    templateUrl: './cxone-game-card.component.html',
    styleUrl: './cxone-game-card.component.scss',
})

export class CxoneGameCardComponent {
    @Input() game: GameObject;
    @Input() gameStatus: string;
    @Input() rankingData: RankingByLogin;

	@ViewChild('tooltipText') tooltipText!: ElementRef;
    @ViewChild('coinsTooltipContainer') coinsTooltipContainer: ElementRef<HTMLElement>;
	@ViewChild('coinsTooltipText') coinsTooltipText: ElementRef<HTMLElement>;
    @ViewChild('tooltip', { static: false }) tooltip!: TooltipDirective;  

    DateFormat = "MMM d, y";
    isTextTruncated = false;
    GameType = GameType;

    milestoneCoins = 0;
    milestoneBadges: string[] = [];
    gs = null;
    bullsEyeWin = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        public changeDetector: ChangeDetectorRef,
        public cvLocalizationService: CVLocalizationService,
        public cvNumberLocalizePipe: CvNumberLocalizePipe,
        public gamesService: GamesService,
        public navigationService: NavigationService,
    ) {
        this.gs = this.gamesService;
     }

    async ngOnInit() {
        await this.loadLocalization();
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    ngOnDestroy(){}
    
    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('Games');
        }
        catch {
            console.error('Failed to load localization for Games');
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Games", key, defaultValue);
        return baseString;
    }

    formatIntervalType(interval: string): string {
        let response: string = "";
        switch(interval){
            case "Day":
                response = this.getLocalizationString("Daily", "Daily") + ":";
                break;
            case "Week": 
                response = this.getLocalizationString("Weekly", "Weekly") + ":";
                break;
            case "Month":
                response = this.getLocalizationString("Monthly", "Monthly") + ":";
                break;
        }
        return response;
    }

    gameDateFormat(date: string): string {
        return formatDate(date, this.DateFormat, this.locale);
    }

    public getWidgetGroupSvgPath(iconName: string): string {
        if (!iconName) return null;
        return `${window['_BaseURI']}/App_Themes/CXone/icons/gamification/games/${iconName}.svg`;
    }
    
    displayTooltip(tooltip: string): string {
        let element = this.tooltipText?.nativeElement;
        this.isTextTruncated = element?.scrollHeight > element?.clientHeight;
        return this.isTextTruncated ? tooltip : '';
    }

    displayCoinTooltip(tooltip: string): string {
        if (this.coinsTooltipContainer && this.coinsTooltipText) {
            return this.coinsTooltipContainer.nativeElement.offsetWidth > this.coinsTooltipText.nativeElement.offsetWidth ? tooltip : "";
        }
        return "";
    }

    getEarnedCoins(): string {
        if( !this.rankingData || !this.game) {
            return null;
        }
        //Validate Qualifier
        let qualifierValue = this.calculateQualifierValue(this.rankingData);
        let qualifierStatus = this.gs.isQualified(qualifierValue, this.game, this.rankingData);

        switch (this.game?.underlyingRule?.GameType) {
            case this.GameType?.Connect3:
            case this.GameType?.Blackout:
                let connect3Positions = this.getConnect3Positions();
                const rewardType = this.game?.underlyingRule?.GameType === this.GameType?.Connect3 ? 'ThreeInRow' : 'Blackout';
        
                if (connect3Positions && rewardType && qualifierStatus) {
                    const isWinner = this.game?.underlyingRule?.GameType === this.GameType?.Connect3 
                        ? this.gamesService.isWinnerConnect3(connect3Positions) 
                        : this.gamesService.isWinnerBlackout(connect3Positions);
                    
                    return this.cvNumberLocalizePipe.transform(
                        isWinner ? this.game?.Connect3Rewards?.[rewardType]?.coins : 0
                    );
                }
                return this.cvNumberLocalizePipe.transform(0);
        
            case this.GameType?.TopDog:
            case this.GameType?.TopTeam:
                if (
                    this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.length > 0 &&
                    qualifierStatus &&
                    this.getRankingValue(this.game?.underlyingRule?.GameType) !== undefined
                ) {
                    if (
                        this.rankingData?.gameRewards?.MyRankingObj?.MyRanking <= 5 &&
                        this.rankingData?.gameRewards?.MyRankingObj?.MyRanking <= this.game?.TopDogRewards?.length
                    ) {
                        return this.cvNumberLocalizePipe.transform(
                            this.game?.TopDogRewards?.[this.rankingData?.gameRewards?.MyRankingObj?.MyRanking - 1]?.Coins?.rewardValue
                        );
                    } else {
                        return this.cvNumberLocalizePipe.transform(this.rankingData?.gameRewards?.MyRankingObj?.CoinsAmount);
                    }
                }
                return this.cvNumberLocalizePipe.transform(0);
        
            case this.GameType?.Bullseye:
                if (qualifierStatus) {
                    for (let i = this.game?.BullseyeRewards?.length - 1; i >= 0; i--) {
                        if (this.isWinnerBullseye(i)) {
                            this.bullsEyeWin = true;
                            return this.cvNumberLocalizePipe.transform(this.rankingData?.gameRewards?.MyRankingObj?.CoinsAmount);
                        }
                    }
                }
                return this.cvNumberLocalizePipe.transform(0);
        
            case this.GameType?.Milestone:
                if (qualifierStatus) {
                    this.getMilestoneEarnings();
                    return this.cvNumberLocalizePipe.transform(this.milestoneCoins);
                }
                return this.cvNumberLocalizePipe.transform(0);
        
            default:
                if (qualifierStatus) {
                    return this.cvNumberLocalizePipe.transform(this.rankingData?.gameRewards?.MyRankingObj?.CoinsAmount);
                }
                return this.cvNumberLocalizePipe.transform(0);
        }
    }

    getEarnedBadges(): string[] {
        let badge: string[] = [];
        if( !this.rankingData || !this.game) {
            return null;
        }

        //Validate Qualifier
        let qualifierValue = this.calculateQualifierValue(this.rankingData);
        let qualifierStatus = this.gs.isQualified(qualifierValue, this.game, this.rankingData);

        switch (this.game?.underlyingRule?.GameType) {
            case this.GameType?.Connect3:
            case this.GameType?.Blackout: {
                let connect3Positions = this.getConnect3Positions();
                const rewardType = this.game?.underlyingRule?.GameType === this.GameType?.Connect3 ? 'ThreeInRow' : 'Blackout';
        
                if (connect3Positions && rewardType && this.game?.Connect3Rewards?.[rewardType]?.badge != 0 && qualifierStatus) {
                    if (this.game?.underlyingRule?.GameType === this.GameType?.Connect3 && this.gamesService.isWinnerConnect3(connect3Positions)) {
                        badge.push('../Admin/ImageResizer.aspx?imgid=' + this.game?.Connect3Rewards[rewardType]?.badge + '&w=24&h=24');
                    } else if (this.game?.underlyingRule?.GameType === this.GameType?.Blackout && this.gamesService.isWinnerBlackout(connect3Positions)) {
                        badge.push('../Admin/ImageResizer.aspx?imgid=' + this.game?.Connect3Rewards[rewardType]?.badge + '&w=24&h=24');
                    }
                }
                return badge;
            }
        
            case this.GameType?.TopDog:
            case this.GameType?.TopTeam: {
                if (this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.length > 0 && qualifierStatus && this.getRankingValue(this.game?.underlyingRule?.GameType) !== undefined) {
                    const topDogBadge = this.game?.TopDogRewards?.[this.rankingData?.gameRewards?.MyRankingObj?.MyRanking - 1]?.Badge?.rewardValue;
                    if (this.rankingData?.gameRewards?.MyRankingObj?.MyRanking <= 5 && topDogBadge && this.rankingData?.gameRewards?.MyRankingObj?.MyRanking <= this.game?.TopDogRewards?.length) {
                        badge.push('../Admin/ImageResizer.aspx?imgid=' + topDogBadge + '&w=24&h=24');
                    }
                    const fallbackBadge = this.rankingData?.gameRewards?.MyRankingObj?.Badges[0]?.ImageID;
                    if (this.rankingData?.gameRewards?.MyRankingObj?.MyRanking > 5 && fallbackBadge) {
                        badge.push('../Admin/ImageResizer.aspx?imgid=' + fallbackBadge + '&w=24&h=24');
                    }
                }
                return badge;
            }
        
            case this.GameType?.Bullseye: {
                if (qualifierStatus && this.bullsEyeWin && this.rankingData?.gameRewards?.MyRankingObj?.Badges?.length > 0) {
                    badge.push('../Admin/ImageResizer.aspx?imgid=' + this.rankingData?.gameRewards?.MyRankingObj?.Badges[0]?.ImageID + '&w=24&h=24');
                }
                return badge;
            }

            case this.GameType?.Milestone: {
                if (qualifierStatus) {
                    return this.milestoneBadges?.filter(badge => badge).reverse() || [];
                }
                return badge;
            }
            
            default: {
                if (this.rankingData?.gameRewards?.MyRankingObj?.Badges && this.rankingData?.gameRewards?.MyRankingObj?.Badges?.length > 0 && qualifierStatus) {
                    const badgeImageID = this.rankingData?.gameRewards?.MyRankingObj?.Badges[0]?.ImageID;
                    if (badgeImageID) {
                        badge.push('../Admin/ImageResizer.aspx?imgid=' + badgeImageID + '&w=24&h=24');
                    }
                }
                return badge;
            }
        }
    }

    getMilestoneEarnings(): void {
        let isWinner: boolean;
        let coins = 0;
        let badges: string[] = [];

        if (!this.rankingData?.gameRewards?.MyRankingObj || this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.length === 0) {
            this.milestoneCoins = coins;
            this.milestoneBadges = badges;
        }

        let isQualified: boolean | null = null;
        if (this.game?.Qualifier !== null) {
            isQualified = this.gamesService.isQualified(this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.[1]?.MyMetricValue, this.game, this.rankingData);
        }

        const metricValue = this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.[0]?.MyMetricValue;

        for (let index = 0; index < this.game?.MilestoneRewards?.length; index++) {
            let { Val1: minValue, Val2: maxValue } = this.game?.MilestoneRewards?.[index]?.Target ?? {};

            if (this.game?.SelectedMetrics?.[0]?.direction === MetricDirection?.Up) {
                if (this.game?.MilestoneRewards?.[index]?.ComparisonType === ComparisonType?.GreaterThan) {
                    isWinner = metricValue >= minValue;
                } else {
                    isWinner = metricValue >= maxValue || (metricValue >= minValue && metricValue <= maxValue);
                }
            } else {
                if (this.game?.MilestoneRewards?.[index]?.ComparisonType === ComparisonType.LessThan) {
                    isWinner = metricValue <= minValue;
                } else {
                    isWinner = metricValue <= maxValue || (metricValue <= maxValue && metricValue >= minValue);
                }
            }

            if (isWinner) {
                coins += this.game?.MilestoneRewards?.[index]?.Coins?.rewardValue ?? 0;    
                const badgeRewardValue = this.game?.MilestoneRewards?.[index]?.Badge?.rewardValue;
                if (badgeRewardValue) {
                    badges.push('../Admin/ImageResizer.aspx?imgid=' + badgeRewardValue + '&w=24&h=24');
                }
            }
        }

        if (this.game?.Qualifier !== null && isQualified && isWinner) {
            this.milestoneCoins = coins;
            this.milestoneBadges = badges;
        } else if (this.game?.Qualifier === null && isQualified === null && isWinner) {
            this.milestoneCoins = coins;
            this.milestoneBadges = badges;
        }
    }

    getBadgeImage(badge: string) {
        let smallImage = badge;
        smallImage = badge.replace('w=24&h=24', 'w=64&h=64');
        return '<img src="' + smallImage + '">';
    }

    calculateQualifierValue(rankingData) {    
        const metricGroupInfos = rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos;
        return metricGroupInfos?.[metricGroupInfos.length - 1]?.MyMetricValue || 0;
    }

    isWinnerBullseye(index: number): boolean {
        if (
            !this.rankingData?.gameRewards?.MyRankingObj ||
            !this.game?.BullseyeRewards[index] ||
            this.game === null ||
            this.rankingData === null ||
            this.gameStatus === "Upcoming"
        ) {
            return false;
        }
    
        const reward = this.game.BullseyeRewards[index];
        const score = this.rankingData.gameRewards.MyRankingObj.MetricGroupInfos[0]?.MyMetricValue;
        const qualifierScore = this.rankingData.gameRewards.MyRankingObj.MetricGroupInfos[1]?.MyMetricValue;
    
        if (score === undefined || score === null) {
            return false;
        }
    
        if (this.game.Qualifier !== null && !this.gs.isQualified(qualifierScore, this.game, this.rankingData)) {
            return false;
        }
    
        const isUpDirection = this.game.SelectedMetrics[0].direction === MetricDirection.Up;
    
        switch (reward.ComparisonType) {
            case ComparisonType.GreaterThan:
                return score > reward.Target.Val1;
            case ComparisonType.LessThan:
                return score < reward.Target.Val1;
            case ComparisonType.Between:
                return isUpDirection
                    ? score > reward.Target.Val1 && score < reward.Target.Val2
                    : score > reward.Target.Val2 && score < reward.Target.Val1;
            default:
                return false;
        }
    }
    
    getRankingValue(game: number) {
        if (game === this.GameType?.TopTeam) {
            return this.rankingData?.gameRewards?.Rankings?.find(item => item.GroupID === this.rankingData?.gameRewards?.MyRankingObj?.MatchingRankLevelID);
        }
        return this.rankingData?.gameRewards?.Rankings?.find(item => item.LoginID === this.navigationService.loggedInID);
    } 

    getConnect3Positions(): boolean[] {
        let connect3Positions = [];
        for (let i = 0; i < this.game?.Connect3Positions?.length; i++) {
            let metricData = null;
            if (this.rankingData != null && this.rankingData?.gameRewards?.MyRankingObj != null) {
                metricData = this.rankingData?.gameRewards?.MyRankingObj?.MetricGroupInfos?.filter((a: any) => {
                    return a.MetricID === this.game?.Connect3Positions[i]?.metric?.metricID;
                })[0];
            }
            if (this.game?.Connect3Positions[i]?.metric === null) {
                connect3Positions[this.game?.Connect3Positions[i]?.position] = false;
            }
            if (this.game?.Connect3Positions[i]?.metric !== null && this.game?.Connect3Positions[i]?.metric?.metricID > -1 && metricData === null) {
                connect3Positions[this.game?.Connect3Positions[i]?.position] = false;
            }
            if (this.game?.Connect3Positions[i]?.metric !== null && this.game?.Connect3Positions[i]?.metric?.metricID > -1 && metricData !== null) {
                if (this.game?.Connect3Positions[i]?.targetObj?.Direction == 0) {
                    if (metricData?.MyMetricValue <= this.game?.Connect3Positions[i]?.targetObj?.Target) {
                        connect3Positions[this.game?.Connect3Positions[i]?.position] = true;
                    } else {
                        connect3Positions[this.game?.Connect3Positions[i]?.position] = false;
                    }
                } else {
                    if (metricData?.MyMetricValue >= this.game?.Connect3Positions[i]?.targetObj?.Target) {
                        connect3Positions[this.game?.Connect3Positions[i]?.position] = true;
                    } else {
                        connect3Positions[this.game?.Connect3Positions[i]?.position] = false;
                    }
                }
            }
        }
        if (this.game?.Connect3Positions?.length == 8) { connect3Positions[4] = true; };
        return connect3Positions;
    }
}
